// src/components/LoggedTicketViewPage/LoggedTicketViewPage.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import { format } from "date-fns";
import PortalSidebar from "../PortalSidebar/PortalSidebar";

export default function LoggedTicketViewPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const fmt = (d) => {
    if (!d) return "—";
    try {
      return format(new Date(d), "dd MMM yyyy HH:mm");
    } catch {
      return "—";
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/");
    setLoading(true);

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };
      const [tRes, cRes] = await Promise.all([
        fetch(`https://portal.gmacc.co.za/api/loggedtickets/${ticketId}/`, {
          headers,
        }),
        fetch(
          `https://portal.gmacc.co.za/api/loggedtickets/${ticketId}/comments/`,
          { headers }
        ),
      ]);

      if (!tRes.ok || !cRes.ok) throw new Error();

      const tData = await tRes.json();
      const cData = await cRes.json();

      setTicket(tData);
      setComments(cData.value || cData);
    } catch {
      setError("Could not load ticket.");
    } finally {
      setLoading(false);
    }
  };

  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);
  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
            ? name
            : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  useEffect(() => {
    document.title = "Ticket Detail";
    fetchData();
  }, [ticketId]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    const token = localStorage.getItem("token");

    try {
      // 1) Post the comment text
      const res = await fetch(
        `https://portal.gmacc.co.za/api/loggedtickets/${ticketId}/newcomments/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ comment: newComment.trim() }),
        }
      );
      if (!res.ok) throw new Error("Comment post failed");
      let commentId;
      if (res.status !== 204) {
        // normal case: server returned JSON
        const created = await res.json();
        console.log(created);
        commentId = created.cj_loggedticketcommentid;
      } else {
        // 204 No Content: re‐fetch the comment list and take the newest one

        const listRes = await fetch(
          `https://portal.gmacc.co.za/api/loggedtickets/${ticketId}/comments/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        const listData = await listRes.json();
        const allComments = listData.value || listData;
        allComments.sort((a, b) => {
          const num = (c) =>
            parseInt(c.cj_commentno.replace(/\D/g, ""), 10) || 0;
          return num(b) - num(a);
        });
        commentId = allComments[0]?.cj_loggedticketcommentid;
        console.log(listData);
      }
      // 2) If there's an attachment, upload it
      if (attachment) {
        const formData = new FormData();
        formData.append("comment_id", commentId);
        formData.append("file", attachment);

        const uploadRes = await fetch(
          `https://portal.gmacc.co.za/api/${commentId}/upload-comment-attachment/`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (!uploadRes.ok) {
          console.warn("Attachment upload failed for comment", commentId);
        }
      }

      // 3) Clear inputs & refresh
      setNewComment("");
      setAttachment(null);
      await fetchData();
    } catch (err) {
      console.error(err);
      alert("Failed to post comment or upload attachment.");
    } finally {
      setPosting(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!window.confirm("Are you sure you want to close this ticket?")) return;
    setClosing(true);
    const token = localStorage.getItem("token");

    try {
      // pull the formatted “closed by” name from the ticket record
      const closerName =
        ticket[
          "_cr446_loggedby_value@OData.Community.Display.V1.FormattedValue"
        ] || "Owner";
      const closeComment = `${closerName} closed the ticket.`;

      // single call to your /close/ endpoint
      const res = await fetch(
        `https://portal.gmacc.co.za/api/loggedtickets/${ticketId}/close/`,
        {
          method: "POST", // your view is @api_view(['POST'])
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ comment: closeComment }),
        }
      );

      if (res.status !== 200 && res.status !== 204) {
        const errText = await res.text();
        throw new Error(`Ticket close failed: ${res.status} ${errText}`);
      }

      // reload everything
      await fetchData();
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }
  if (error) {
    return <p className={styles.errorText}>{error}</p>;
  }

  const detailFields = [
    ["Ticket Number", "cr446_ticketnumber"],
    ["Assigned To", "_cj_assignedto_value"],
    ["Category", "cr446_ticketcategory"],
    ["Status", "cr446_progress"],
    ["Building", "_cr446_relatedbuilding_value"],
  ];

  const readFormatted = (obj, field) =>
    obj[`${field}@OData.Community.Display.V1.FormattedValue`] ??
    obj[field] ??
    "—";

  // sort comments newest first
  const sortedComments = [...comments].sort(
    (a, b) => new Date(b.createdon) - new Date(a.createdon)
  );

  const ticketPortal =
  Number(ticket?.cr446_tickettype) === 156310001 ? "owner" : "trustee";
  return (
    <div className={styles.page}>
      {/* NAVBAR */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>
            Ticket {readFormatted(ticket, "cr446_ticketnumber")}
          </h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      <div className={styles.shell}>
        <PortalSidebar
  activePortal={ticketPortal}
  activeTab="tickets"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole={ticketPortal}
/>
        {/* DETAILS TABLE */}
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            <div className={styles.paymentHeaderCard}>
              <div className={styles.card}>
                <button
                  className={styles.paymentBackBtn}
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>

                {/* TITLE */}
                <h1 className={styles.paymentHeaderTitle}>TICKET DETAILS</h1>

                {/* DIVIDER */}
                <div className={styles.paymentHeaderDivider} />

                {/* LABEL ROW — AUTO GRID */}
                <div
                  className={styles.paymentHeaderGridAuto}
                  style={{ "--col-count": detailFields.length }}
                >
                  {detailFields.map(([label]) => (
                    <div key={label} className={styles.paymentHeaderLabelItem}>
                      {label}:
                    </div>
                  ))}
                </div>

                {/* VALUE PILL — AUTO GRID */}
                <div className={styles.paymentHeaderValuePill}>
                  <div
                    className={styles.paymentHeaderGridAuto}
                    style={{ "--col-count": detailFields.length }}
                  >
                    {detailFields.map(([label, field]) => {
                      const value = readFormatted(ticket, field);

                      const isBuilding = label === "Building";

                      return (
                        <div
                          key={field}
                          className={styles.paymentHeaderValueItem}
                        >
                          {isBuilding ? (
                            ticket[field] ? (
                              <span
                                className={styles.gmaLink}
                                onClick={() => {
                                  if (userAccess.includes("owner")) {
                                    navigate(
                                      `/building-owner-info/${ticket[field]}`
                                    );
                                  } else {
                                    navigate(`/building-info/${ticket[field]}`);
                                  }
                                }}
                              >
                                {value}
                              </span>
                            ) : (
                              value
                            )
                          ) : (
                            value
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* DESCRIPTION CARD */}
            <div className={styles.paymentCardGrid}>
              <div
                className={styles.paymentCardDark}
                style={{ marginBottom: "24px" }}
              >
                <h2 className={styles.cardSectionTitle}>Description</h2>

                <div className={styles.inputGroup}>
                  <h3 className={styles.inputLabel}>Details:</h3>

                  <div
                    className={styles.inputField}
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {readFormatted(ticket, "cr446_ticketdescription")}
                  </div>
                </div>
              </div>

              {/* COMMENTS CARD */}

              <div className={styles.paymentCard}>
                <h2 className={styles.cardSectionTitleLight}>Comments</h2>

                {sortedComments.length === 0 ? (
                  <p className={styles.infoText}>No comments yet.</p>
                ) : (
                  <div className={styles.responsiveTableWrapper}>
                    <table
                      className={`${styles.dataTable} ${styles.commentsTable}`}
                    >
                      <thead>
                        <th>Comment Number</th>
                        <th>Comment</th>
                        <th>Attachment</th>
                        <th>Created On</th>
                      </thead>

                      <tbody>
                        {sortedComments.map((c) => (
                          <tr
                            key={c.cj_loggedticketcommentid}
                            className={styles.topTableRow}
                          >
                            <td>{c.cj_commentno}</td>

                            <td className={styles.wrapCell}>{c.cj_comment}</td>

                            <td>
                              {c.cj_attachmentlink ? (
                                <a
                                  href={c.cj_attachmentlink}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Link
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>

                            <td className={styles.topTableCell}>
                              {fmt(c.createdon)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ADD COMMENT — Style 5 */}
                <div
                  className={styles.inputGroupLight}
                  style={{ marginTop: "24px" }}
                >
                  <h3 className={styles.inputLabelLight}>Add Comment:</h3>
                  <textarea
                    className={styles.editableTextarea}
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    style={{ minHeight: "90px" }}
                  />
                </div>

                <div className={styles.inputGroupLight}>
                  <h3 className={styles.inputLabelLight}>Attachment:</h3>
                  <input
                    type="file"
                    className={styles.inputFieldLight}
                    onChange={(e) => setAttachment(e.target.files[0] || null)}
                  />
                </div>

                <div
                  className={styles.inputGroupLight}
                  style={{ display: "flex", gap: "12px" }}
                >
                  <button
                    className={styles.inputFieldLight}
                    onClick={handleAddComment}
                    disabled={posting || !newComment.trim()}
                  >
                    {posting ? "Posting…" : "Add Comment"}
                  </button>

                  {userAccess.includes("owner") && (
                    <button
                      className={styles.inputFieldLight}
                      onClick={handleCloseTicket}
                      disabled={posting || closing}
                      style={{
                        background: "#dc3545",
                        color: "#fff",
                      }}
                    >
                      {closing ? "Closing…" : "Close Ticket"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
